/**
 * 文档分析页面 - 支持六个Tab页展示：项目概述、技术评分要求、目录要求、废标项、分项报价、招标模版
 */
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { documentApi, AnalysisRequest, projectApi, tokenApi } from '../services/api';
import { DocumentIcon, ClipboardDocumentIcon, PencilIcon, ArrowPathIcon, SparklesIcon, CheckIcon, ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Toast, { ToastType } from '../components/Toast';
import TokenAlertDialog from '../components/TokenAlertDialog';
import useTokenAlert from '../hooks/useTokenAlert';

interface DocumentAnalysisProps {
  fileContent: string;
  projectOverview: string;
  techRequirements: string;
  outlineRequirements: string;
  disqualification: string;
  quotation: string; // 新增：分项报价
  templates: string; // 新增：招标模版
  selectedModel: string; // 项目配置的AI模型名称
  projectId?: string; // 新增：项目ID
  tokenBalance?: number | null; // Token余额
  onAnalysisComplete: (overview: string, requirements: string, outlineReqs: string, disqualification: string) => void;
  onTabAnalysisComplete?: (tabType: string, content: string) => void;
  onProjectNameExtracted?: (name: string) => void; // 新增：项目名称提取回调
  onNext?: () => void;
  onPrev?: () => void;
  nextDisabled?: boolean;
}

type TabType = 'overview' | 'requirements' | 'outline' | 'disqualification' | 'quotation' | 'templates';

const tabs = [
  { id: 'overview' as const, label: '项目概述', color: 'blue' as const },
  { id: 'requirements' as const, label: '技术评分要求', color: 'green' as const },
  { id: 'outline' as const, label: '目录要求', color: 'purple' as const },
  { id: 'disqualification' as const, label: '废标项', color: 'red' as const },
  { id: 'quotation' as const, label: '分项报价', color: 'orange' as const },
  { id: 'templates' as const, label: '招标模版', color: 'indigo' as const },
];

const DocumentAnalysis: React.FC<DocumentAnalysisProps> = ({
  fileContent,
  projectOverview,
  techRequirements,
  outlineRequirements,
  disqualification,
  quotation, // 新增
  templates, // 新增：招标模版
  selectedModel, // 项目配置的AI模型名称
  projectId, // 新增：项目ID
  tokenBalance, // Token余额
  onAnalysisComplete,
  onTabAnalysisComplete,
  onProjectNameExtracted, // 新增
  onNext,
  onPrev,
  nextDisabled = false,
}) => {
  const [analyzing, setAnalyzing] = useState(false);

  // Token 欠费弹窗（充值跳转主站，见 useTokenAlert）
  const { tokenAlert, setTokenAlert, closeAlert: closeTokenAlert, handleRecharge: rechargeTokens } = useTokenAlert();
  
  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    messages?: string[];
  }>({
    show: false,
    type: 'info',
    title: '',
    messages: [],
  });

  const [localOverview, setLocalOverview] = useState(projectOverview);
  const [localRequirements, setLocalRequirements] = useState(techRequirements);
  const [localOutlineReqs, setLocalOutlineReqs] = useState(outlineRequirements);
  const [localDisqualification, setLocalDisqualification] = useState(disqualification);
  const [localQuotation, setLocalQuotation] = useState(quotation); // 使用 props 初始化
  const [localTemplates, setLocalTemplates] = useState(templates); // 新增：招标模版

  // 当前Tab页
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // 判断必填Tab是否有任意一个为空（招标模版为选填，其余5个必填）
  const hasEmptyRequiredTab = !localOverview?.trim() ||
    !localRequirements?.trim() ||
    !localOutlineReqs?.trim() ||
    !localDisqualification?.trim() ||
    !localQuotation?.trim();

  // 收集具体哪些必填Tab为空，用于提示
  const emptyTabNames: string[] = [];
  if (!localOverview?.trim()) emptyTabNames.push('项目概述');
  if (!localRequirements?.trim()) emptyTabNames.push('技术评分要求');
  if (!localOutlineReqs?.trim()) emptyTabNames.push('目录要求');
  if (!localDisqualification?.trim()) emptyTabNames.push('废标项');
  if (!localQuotation?.trim()) emptyTabNames.push('分项报价');

  // 内容区域的ref，用于滚动控制
  const overviewRef = useRef<HTMLDivElement>(null);
  const requirementsRef = useRef<HTMLDivElement>(null);
  const outlineRef = useRef<HTMLDivElement>(null);
  const disqualificationRef = useRef<HTMLDivElement>(null);
  const quotationRef = useRef<HTMLDivElement>(null); // 新增：分项报价
  const templatesRef = useRef<HTMLDivElement>(null); // 新增：招标模版

  // 编辑模式状态
  const [isEditing, setIsEditing] = useState(false);
  const [editingContent, setEditingContent] = useState('');

  // 处理换行符的函数
  const normalizeLineBreaks = (text: string) => {
    if (!text) return text;
    return text
      .replace(/\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');
  };

  // 从项目概述中提取项目名称并更新到数据库
  const extractAndUpdateProjectName = async (overviewContent: string) => {
    if (!overviewContent || !projectId) return;

    // 从项目概述中提取项目名称
    const projectNameMatch = overviewContent.match(/项目名称[：:]\s*([^\n]+)/);
    if (projectNameMatch && projectNameMatch[1]) {
      const extractedName = projectNameMatch[1].trim()
        .replace(/\*\*/g, '')  // 移除Markdown粗体标记
        .replace(/^[\d、\.]\s*/, '');  // 移除前导编号

      // 如果提取到了项目名称，更新到数据库
      if (extractedName && extractedName !== '未知项目') {
        try {
          await projectApi.updateProject(projectId, {
            name: extractedName
          });

          // 通知父组件项目名称已更新
          if (onProjectNameExtracted) {
            onProjectNameExtracted(extractedName);
          }
        } catch (error) {
          console.error('更新项目名称失败:', error);
        }
      }
    }
  };

  // 流式显示状态（改为并行生成）
  const [generatingTabs, setGeneratingTabs] = useState<Set<TabType>>(new Set()); // 正在生成的Tab集合
  const [streamingOverview, setStreamingOverview] = useState('');
  const [streamingRequirements, setStreamingRequirements] = useState('');
  const [streamingOutline, setStreamingOutline] = useState('');
  const [streamingDisqualification, setStreamingDisqualification] = useState('');
  const [streamingQuotation, setStreamingQuotation] = useState(''); // 新增：分项报价流式内容
  const [streamingTemplates, setStreamingTemplates] = useState(''); // 新增：招标模版流式内容

  // 流式内容转 HTML（用 dangerouslySetInnerHTML 绕过 React 协调）
  const streamingToHtml = (content: string): string => {
    try {
      return marked(content, { gfm: true, breaks: true }) as string;
    } catch {
      return content;
    }
  };

  // 监听 props 变化，从数据库恢复时更新本地状态
  // 但只在没有Tab正在生成时才同步，避免覆盖正在生成的内容
  useEffect(() => {
    // 只在没有任何Tab正在生成时才同步props
    if (generatingTabs.size === 0 && !analyzing) {
      setLocalOverview(projectOverview);
      setLocalRequirements(techRequirements);
      setLocalOutlineReqs(outlineRequirements);
      setLocalDisqualification(disqualification);
      setLocalQuotation(quotation);
      setLocalTemplates(templates);
    }
  }, [projectOverview, techRequirements, outlineRequirements, disqualification, quotation, templates, generatingTabs, analyzing]);

  // 滚动到底部的函数
  const scrollToBottom = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  };

  // 滚动到顶部的函数
  const scrollToTop = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollTop = 0;
    }
  };

  // 当流式内容更新时，自动滚动到底部
  useEffect(() => {
    if (analyzing && generatingTabs.has('overview')) {
      scrollToBottom(overviewRef);
    }
  }, [streamingOverview, analyzing, generatingTabs]);

  useEffect(() => {
    if (analyzing && generatingTabs.has('requirements')) {
      scrollToBottom(requirementsRef);
    }
  }, [streamingRequirements, analyzing, generatingTabs]);

  useEffect(() => {
    if (analyzing && generatingTabs.has('outline')) {
      scrollToBottom(outlineRef);
    }
  }, [streamingOutline, analyzing, generatingTabs]);

  useEffect(() => {
    if (analyzing && generatingTabs.has('disqualification')) {
      scrollToBottom(disqualificationRef);
    }
  }, [streamingDisqualification, analyzing, generatingTabs]);

  useEffect(() => {
    if (analyzing && generatingTabs.has('quotation')) {
      scrollToBottom(quotationRef);
    }
  }, [streamingQuotation, analyzing, generatingTabs]);

  useEffect(() => {
    if (analyzing && generatingTabs.has('templates')) {
      scrollToBottom(templatesRef);
    }
  }, [streamingTemplates, analyzing, generatingTabs]);

  // 显示 Toast
  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  // 关闭 Toast
  const closeToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // 复制内容
  const handleCopy = () => {
    let content = '';
    if (activeTab === 'overview') content = localOverview;
    else if (activeTab === 'requirements') content = localRequirements;
    else if (activeTab === 'outline') content = localOutlineReqs;
    else if (activeTab === 'disqualification') content = localDisqualification;
    else if (activeTab === 'quotation') content = localQuotation;
    else if (activeTab === 'templates') content = localTemplates;

    if (content) {
      navigator.clipboard.writeText(content).then(() => {
        showToast('success', '复制成功！');
      }).catch(() => {
        showToast('error', '复制失败，请重试');
      });
    } else {
      showToast('error', '当前内容为空');
    }
  };

  // 编辑内容
  const handleEdit = () => {
    // 获取当前Tab的内容
    let content = '';
    if (activeTab === 'overview') content = localOverview;
    else if (activeTab === 'requirements') content = localRequirements;
    else if (activeTab === 'outline') content = localOutlineReqs;
    else if (activeTab === 'disqualification') content = localDisqualification;
    else if (activeTab === 'quotation') content = localQuotation;
    else if (activeTab === 'templates') content = localTemplates;

    setEditingContent(content);
    setIsEditing(true);
  };

  // 保存编辑
  const handleSave = () => {
    // 更新对应Tab的内容
    if (activeTab === 'overview') {
      setLocalOverview(editingContent);
      onAnalysisComplete(
        normalizeLineBreaks(editingContent),
        localRequirements,
        localOutlineReqs,
        localDisqualification
      );
    } else if (activeTab === 'requirements') {
      setLocalRequirements(editingContent);
      onAnalysisComplete(
        localOverview,
        normalizeLineBreaks(editingContent),
        localOutlineReqs,
        localDisqualification
      );
    } else if (activeTab === 'outline') {
      setLocalOutlineReqs(editingContent);
      onAnalysisComplete(
        localOverview,
        localRequirements,
        normalizeLineBreaks(editingContent),
        localDisqualification
      );
    } else if (activeTab === 'disqualification') {
      setLocalDisqualification(editingContent);
      onAnalysisComplete(
        localOverview,
        localRequirements,
        localOutlineReqs,
        normalizeLineBreaks(editingContent)
      );
    } else if (activeTab === 'quotation') {
      setLocalQuotation(editingContent);
      // 分项报价通过 onTabAnalysisComplete 保存
      onTabAnalysisComplete?.('quotation', editingContent);
    } else if (activeTab === 'templates') {
      setLocalTemplates(editingContent);
      // 招标模版通过 onTabAnalysisComplete 保存
      onTabAnalysisComplete?.('templates', editingContent);
    }

    setIsEditing(false);
    setEditingContent('');
    showToast('success', '保存成功！');
  };

  // 重新生成当前Tab内容
  const handleRegenerate = async () => {
    if (!fileContent.trim()) {
      showToast('error', '请先上传文件内容');
      return;
    }

    setAnalyzing(true);

    try {
      let analysisType: 'overview' | 'requirements' | 'outline_requirements' | 'disqualification' | 'quotation' | 'templates' = 'overview';
      if (activeTab === 'overview') analysisType = 'overview';
      else if (activeTab === 'requirements') analysisType = 'requirements';
      else if (activeTab === 'outline') analysisType = 'outline_requirements';
      else if (activeTab === 'disqualification') analysisType = 'disqualification';
      else if (activeTab === 'quotation') analysisType = 'quotation';
      else if (activeTab === 'templates') analysisType = 'templates';

      // 标记当前Tab正在生成
      setGeneratingTabs(new Set([activeTab]));

      const request: AnalysisRequest = {
        file_content: fileContent,
        analysis_type: analysisType,
        selected_model: selectedModel,
        project_id: projectId // 传递项目ID，用于查询结构化内容
      };

      const stream = await documentApi.analyzeDocumentStream(request);
      let content = '';

      // 清空当前Tab的流式内容
      if (activeTab === 'overview') setStreamingOverview('');
      else if (activeTab === 'requirements') setStreamingRequirements('');
      else if (activeTab === 'outline') setStreamingOutline('');
      else if (activeTab === 'disqualification') setStreamingDisqualification('');
      else if (activeTab === 'quotation') setStreamingQuotation('');
      else if (activeTab === 'templates') setStreamingTemplates('');

      for await (const chunk of streamAsyncIterable(stream)) {
        if (activeTab === 'overview') {
          setStreamingOverview(prev => prev + chunk);
        } else if (activeTab === 'requirements') {
          setStreamingRequirements(prev => prev + chunk);
        } else if (activeTab === 'outline') {
          setStreamingOutline(prev => prev + chunk);
        } else if (activeTab === 'disqualification') {
          setStreamingDisqualification(prev => prev + chunk);
        } else if (activeTab === 'quotation') {
          setStreamingQuotation(prev => prev + chunk);
        } else if (activeTab === 'templates') {
          setStreamingTemplates(prev => prev + chunk);
        }
        content += chunk;
      }

      // 更新对应的内容
      if (activeTab === 'overview') {
        setLocalOverview(content);
        // 不立即清空，等待下一次渲染
        scrollToTop(overviewRef);

        // 提取项目名称并更新到数据库
        await extractAndUpdateProjectName(content);

        onAnalysisComplete(
          normalizeLineBreaks(content),
          localRequirements,
          localOutlineReqs,
          localDisqualification
        );
      } else if (activeTab === 'requirements') {
        setLocalRequirements(content);
        // 不立即清空
        scrollToTop(requirementsRef);
        onAnalysisComplete(
          localOverview,
          normalizeLineBreaks(content),
          localOutlineReqs,
          localDisqualification
        );
      } else if (activeTab === 'outline') {
        setLocalOutlineReqs(content);
        // 不立即清空
        scrollToTop(outlineRef);
        onAnalysisComplete(
          localOverview,
          localRequirements,
          normalizeLineBreaks(content),
          localDisqualification
        );
      } else if (activeTab === 'disqualification') {
        setLocalDisqualification(content);
        // 不立即清空
        scrollToBottom(disqualificationRef);
        onAnalysisComplete(
          localOverview,
          localRequirements,
          localOutlineReqs,
          normalizeLineBreaks(content)
        );
      } else if (activeTab === 'quotation') {
        setLocalQuotation(content);
        // 不立即清空
        scrollToBottom(quotationRef);
        onTabAnalysisComplete?.('quotation', content);
      } else if (activeTab === 'templates') {
        setLocalTemplates(content);
        // 不立即清空
        scrollToTop(templatesRef);
        onTabAnalysisComplete?.('templates', content);
      }

      // 单个Tab完成后，延迟清空该Tab的流式内容
      setTimeout(() => {
        if (activeTab === 'overview') setStreamingOverview('');
        else if (activeTab === 'requirements') setStreamingRequirements('');
        else if (activeTab === 'outline') setStreamingOutline('');
        else if (activeTab === 'disqualification') setStreamingDisqualification('');
        else if (activeTab === 'quotation') setStreamingQuotation('');
        else if (activeTab === 'templates') setStreamingTemplates('');
      }, 100); // 等待状态更新完成

      showToast('success', '重新生成完成！');

    } catch (error) {
      console.error('重新生成失败:', error);
      const errorMsg = (error as Error)?.message || '';
      // Token 欠费错误不显示 Toast，会自动弹出欠费弹框
      if (!errorMsg.includes('Token') && !errorMsg.includes('欠费') && !errorMsg.includes('余额')) {
        showToast('error', '重新生成失败，请重试');
      }
    } finally {
      setAnalyzing(false);
      setGeneratingTabs(new Set());
    }
  };

  // 智能优化（待实现）
  const handleOptimize = () => {
    showToast('info', '智能优化功能开发中...');
  };

  // 处理文档分析（并行生成所有Tab）
  const handleAnalysis = async () => {
    if (!fileContent.trim()) {
      showToast('error', '请先上传文件内容');
      return;
    }

    // 检查 Token 余额：如果已欠费（小于0），显示欠费弹窗
    if (tokenBalance !== undefined && tokenBalance !== null && tokenBalance < 0) {
      setTokenAlert({
        show: true,
        type: 'zero',
        title: 'Token 已欠费',
        message: `您的 Token 已欠费 ${Math.abs(tokenBalance).toLocaleString()}，无法使用批量解析功能。请立即充值以恢复使用。`
      });
      return;
    }

    setAnalyzing(true);

    try {
      // 重置流式显示状态
      setStreamingOverview('');
      setStreamingRequirements('');
      setStreamingOutline('');
      setStreamingDisqualification('');
      setStreamingQuotation('');
      setStreamingTemplates('');

      // 标记所有Tab正在生成
      setGeneratingTabs(new Set<TabType>(['overview', 'requirements', 'outline', 'disqualification', 'quotation', 'templates']));

      // 准备所有分析请求
      const analysisRequests: Array<{
        type: TabType;
        analysisType: 'overview' | 'requirements' | 'outline_requirements' | 'disqualification' | 'quotation' | 'templates';
      }> = [
        { type: 'overview', analysisType: 'overview' },
        { type: 'requirements', analysisType: 'requirements' },
        { type: 'outline', analysisType: 'outline_requirements' },
        { type: 'disqualification', analysisType: 'disqualification' },
        { type: 'quotation', analysisType: 'quotation' },
        { type: 'templates', analysisType: 'templates' },
      ];

      // 用于收集结果
      let overview = '';
      let requirements = '';
      let outlineReqs = '';
      let disqualificationData = '';
      let quotationData = '';
      let templatesData = '';

      // 并行启动所有分析任务
      const analysisPromises = analysisRequests.map(async ({ type, analysisType }) => {
        try {
          const request: AnalysisRequest = {
            file_content: fileContent,
            analysis_type: analysisType,
            selected_model: selectedModel,
            project_id: projectId // 传递项目ID，用于查询结构化内容
          };

          const stream = await documentApi.analyzeDocumentStream(request);
          let content = '';

          // 根据类型更新对应的流式内容
          for await (const chunk of streamAsyncIterable(stream)) {
            content += chunk;

            // 更新对应Tab的流式显示
            if (type === 'overview') {
              setStreamingOverview(prev => prev + chunk);
            } else if (type === 'requirements') {
              setStreamingRequirements(prev => prev + chunk);
            } else if (type === 'outline') {
              setStreamingOutline(prev => prev + chunk);
            } else if (type === 'disqualification') {
              setStreamingDisqualification(prev => prev + chunk);
            } else if (type === 'quotation') {
              setStreamingQuotation(prev => prev + chunk);
            } else if (type === 'templates') {
              setStreamingTemplates(prev => prev + chunk);
            }
          }

          // ✅ 流式生成完成后，立即处理该Tab的结果并移除转圈状态
          if (type === 'overview') {
            overview = content;
            setLocalOverview(content);
            // 不立即清空 streaming，让渲染逻辑优先使用 local
            scrollToTop(overviewRef);
            await extractAndUpdateProjectName(content);
            onTabAnalysisComplete?.('overview', normalizeLineBreaks(content));
          } else if (type === 'requirements') {
            requirements = content;
            setLocalRequirements(content);
            // 不立即清空 streaming
            scrollToTop(requirementsRef);
            onTabAnalysisComplete?.('requirements', normalizeLineBreaks(content));
          } else if (type === 'outline') {
            outlineReqs = content;
            setLocalOutlineReqs(content);
            // 不立即清空 streaming
            scrollToTop(outlineRef);
            onTabAnalysisComplete?.('outline_requirements', normalizeLineBreaks(content));
          } else if (type === 'disqualification') {
            disqualificationData = content;
            setLocalDisqualification(content);
            // 不立即清空 streaming
            scrollToBottom(disqualificationRef);
            onTabAnalysisComplete?.('disqualification', normalizeLineBreaks(content));
          } else if (type === 'quotation') {
            quotationData = content;
            setLocalQuotation(content);
            // 不立即清空 streaming
            scrollToTop(quotationRef);
            onTabAnalysisComplete?.('quotation', normalizeLineBreaks(content));
          } else if (type === 'templates') {
            templatesData = content;
            setLocalTemplates(content);
            // 不立即清空 streaming
            scrollToTop(templatesRef);
            onTabAnalysisComplete?.('templates', normalizeLineBreaks(content));
          }

          // ✅ 该Tab生成完成，立即从生成集合中移除
          setGeneratingTabs(prev => {
            const newSet = new Set(prev);
            newSet.delete(type);
            return newSet;
          });

          return { type, content };
        } catch (error) {
          console.error(`${type} 分析失败:`, error);

          // ✅ 即使失败也要移除转圈状态
          setGeneratingTabs(prev => {
            const newSet = new Set(prev);
            newSet.delete(type);
            return newSet;
          });

          return { type, content: '', error };
        }
      });

      // 等待所有分析完成
      const results = await Promise.all(analysisPromises);

      // 检查是否有错误（特别是Token欠费错误）
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        // 检查是否是Token欠费错误
        const tokenError = errors.find(e => {
          const errorMsg = (e.error as Error)?.message || '';
          return errorMsg.includes('Token') || errorMsg.includes('欠费') || errorMsg.includes('余额');
        });
        if (tokenError) {
          // Token错误会通过API拦截器触发token:overdraft事件，这里不需要额外处理
          console.log('Token余额不足，已触发欠费提示');
          return; // 不显示成功消息
        }
      }

      // ✅ 不立即清空流式内容，让渲染逻辑根据优先级自动切换
      // 因为条件是：streamingXXX 存在 ? 显示流式 : localXXX 存在 ? 显示最终
      // 即使 streaming 还有内容，local 也已经设置，下次渲染会优先使用 local

      // 通知父组件（前4个Tab的数据）
      onAnalysisComplete(
        normalizeLineBreaks(overview),
        normalizeLineBreaks(requirements),
        normalizeLineBreaks(outlineReqs),
        normalizeLineBreaks(disqualificationData)
      );

      showToast('success', '解析完成！');

      // 触发Token刷新事件
      window.dispatchEvent(new CustomEvent('token:used'));
      console.log('✨ 招标文件分析完成，触发Token刷新事件');

    } catch (error) {
      console.error('分析失败:', error);
      const errorMsg = (error as Error)?.message || '';
      // Token 欠费错误不显示 Toast，因为会弹出欠费弹框
      if (!errorMsg.includes('Token') && !errorMsg.includes('欠费') && !errorMsg.includes('余额')) {
        showToast('error', '解析失败，请重试');
      }
    } finally {
      setAnalyzing(false);
      setGeneratingTabs(new Set()); // 确保清空
    }
  };

  // 将 Response 对象转换为异步可迭代对象，并解析SSE格式
  async function* streamAsyncIterable(response: Response) {
    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // 将 Uint8Array 转换为字符串并添加到缓冲区
        buffer += decoder.decode(value, { stream: true });

        // 按行分割处理SSE数据
        const lines = buffer.split('\n');
        // 保留最后一个可能不完整的行
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          // 解析 "data: {json}" 格式
          if (trimmedLine.startsWith('data: ')) {
            const jsonStr = trimmedLine.substring(6); // 移除 "data: " 前缀

            // 忽略 [DONE] 标记
            if (jsonStr === '[DONE]') {
              continue;
            }

            try {
              const data = JSON.parse(jsonStr);
              if (data.chunk) {
                yield data.chunk;
              }
            } catch (e) {
              // 忽略JSON解析错误
              console.warn('Failed to parse SSE data:', trimmedLine);
            }
          }
        }
      }

      // 处理缓冲区中剩余的数据
      if (buffer.trim()) {
        const trimmedLine = buffer.trim();
        if (trimmedLine.startsWith('data: ')) {
          const jsonStr = trimmedLine.substring(6);

          // 忽略 [DONE] 标记
          if (jsonStr === '[DONE]') {
            return;
          }

          try {
            const data = JSON.parse(jsonStr);
            if (data.chunk) {
              yield data.chunk;
            }
          } catch (e) {
            console.warn('Failed to parse remaining SSE data:', trimmedLine);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // Markdown渲染组件（默认样式）
  const markdownComponents = {
    p: ({ node, ...props }: any) => <p className="mb-3 leading-relaxed text-gray-900" {...props} />,
    ul: ({ node, ...props }: any) => <ul className="mb-4 ml-6 list-disc text-gray-900" {...props} />,
    ol: ({ node, ...props }: any) => <ol className="mb-4 ml-6 list-decimal text-gray-900" {...props} />,
    li: ({ node, ...props }: any) => <li className="mb-1 text-gray-900" {...props} />,
    strong: ({ node, ...props }: any) => <strong className="font-semibold text-gray-900" {...props} />,
    em: ({ node, ...props }: any) => <em className="italic text-gray-800" {...props} />,
    h1: ({ node, ...props }: any) => <h1 className="text-2xl font-bold mb-4 text-gray-900" {...props} />,
    h2: ({ node, ...props }: any) => <h2 className="text-xl font-semibold mb-3 text-gray-900" {...props} />,
    h3: ({ node, ...props }: any) => <h3 className="text-lg font-medium mb-2 text-gray-900" {...props} />,
    h4: ({ node, ...props }: any) => <h4 className="text-base font-medium mb-2 text-gray-900" {...props} />,
    h5: ({ node, ...props }: any) => <h5 className="text-sm font-medium mb-1 text-gray-900" {...props} />,
    h6: ({ node, ...props }: any) => <h6 className="text-sm font-medium mb-1 text-gray-900" {...props} />,
    code: ({ node, ...props }: any) => <code className="px-1 py-0.5 bg-gray-100 rounded text-sm font-mono text-gray-900" {...props} />,
    pre: ({ node, ...props }: any) => <pre className="p-3 bg-gray-50 rounded overflow-x-auto text-sm text-gray-900" {...props} />,
    // 表格相关组件
    table: ({ node, ...props }: any) => (
      <div className="mb-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300 border border-gray-300" {...props} />
      </div>
    ),
    thead: ({ node, ...props }: any) => <thead className="bg-gray-50" {...props} />,
    tbody: ({ node, ...props }: any) => <tbody className="divide-y divide-gray-200 bg-white" {...props} />,
    tr: ({ node, ...props }: any) => <tr {...props} />,
    th: ({ node, ...props }: any) => <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900 border border-gray-300" {...props} />,
    td: ({ node, ...props }: any) => <td className="px-3 py-2 text-sm text-gray-900 border border-gray-300" {...props} />,
  };

  // 招标模版专用Markdown组件（保留格式，禁用下划线强调解析）
  const templatesMarkdownComponents = {
    ...markdownComponents,
    // 移除代码块背景色
    code: ({ node, inline, ...props }: any) => {
      if (inline) {
        return <code className="text-sm font-mono text-gray-900" {...props} />;
      }
      return <code className="text-sm font-mono text-gray-900 block" {...props} />;
    },
    pre: ({ node, ...props }: any) => (
      <pre 
        className="whitespace-pre font-mono text-sm text-gray-900 leading-relaxed my-2 p-0 bg-transparent border-0" 
        {...props} 
      />
    ),
    thead: ({ node, ...props }: any) => <thead className="bg-gray-100" {...props} />,
  };

  // 高级加载骨架屏组件
  const LoadingSkeleton = ({ type }: { type: 'overview' | 'requirements' | 'outline' | 'disqualification' | 'quotation' | 'templates' }) => {
    const skeletonContent = {
      overview: {
        icon: '📊',
        title: '智能分析项目概述',
        subtitle: 'AI 正在深度解析标书文档，提取关键项目信息...'
      },
      requirements: {
        icon: '⚙️',
        title: '智能解析技术要求',
        subtitle: 'AI 正在识别和细化技术评分要求...'
      },
      outline: {
        icon: '📝',
        title: '智能解析目录结构',
        subtitle: 'AI 正在构建标书目录框架...'
      },
      disqualification: {
        icon: '⚠️',
        title: '智能分析废标项',
        subtitle: 'AI 正在识别和提取废标条款...'
      },
      quotation: {
        icon: '💰',
        title: '智能提取报价要求',
        subtitle: 'AI 正在分析分项报价要求及最高限价...'
      },
      templates: {
        icon: '📋',
        title: '智能提取招标模版',
        subtitle: 'AI 正在从招标文件中提取所有模版内容...'
      }
    };

    const content = skeletonContent[type];

    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-6">
        {/* 主视觉区 */}
        <div className="text-center space-y-4">
          <div className="text-6xl animate-bounce">{content.icon}</div>
          <h3 className="text-xl font-semibold text-gray-700">{content.title}</h3>
          <p className="text-sm text-gray-500 max-w-md px-4">{content.subtitle}</p>
        </div>

        {/* 加载动画 */}
        <div className="flex items-center space-x-2">
          <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse"></div>
          <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
        </div>

        {/* 底部提示 */}
        <div className="text-xs text-gray-400 flex items-center space-x-2 mt-4">
          <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>由 AI 驱动的智能分析</span>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto h-full flex flex-col">
      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
        duration={5000}
      />

      {/* 文档分析区域 */}
      <div className="bg-white rounded-lg shadow p-6 flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">🔍 标书解析</h2>
          <button
            onClick={handleAnalysis}
            disabled={analyzing || !fileContent}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {analyzing ? (
              <>
                <div className="animate-spin -ml-1 mr-2 h-4 w-4 text-white">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                解析中...
              </>
            ) : (
              <>
                <DocumentIcon className="w-4 h-4 mr-2" />
                开始解析
              </>
            )}
          </button>
        </div>

        {/* Tab页导航 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const isGenerating = generatingTabs.has(tab.id); // 该Tab是否正在生成
              const isDisabled = isEditing; // 只有编辑模式禁用切换，生成过程中允许切换
              const colorClasses = {
                blue: isActive ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                green: isActive ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                purple: isActive ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                red: isActive ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                orange: isActive ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                indigo: isActive ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              };

              return (
                <button
                  key={tab.id}
                  onClick={() => !isDisabled && setActiveTab(tab.id)}
                  disabled={isDisabled}
                  className={`${colorClasses[tab.color]} whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                    isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  {tab.label}
                  {isGenerating && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* 功能按钮 */}
        <div className="flex items-center justify-end gap-2 py-2">
          {!isEditing ? (
            <>
              <button
                onClick={handleCopy}
                disabled={generatingTabs.has(activeTab)}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="复制当前内容"
              >
                <ClipboardDocumentIcon className="w-3.5 h-3.5 mr-1" />
                复制
              </button>

              <button
                onClick={handleEdit}
                disabled={generatingTabs.has(activeTab)}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="编辑当前内容"
              >
                <PencilIcon className="w-3.5 h-3.5 mr-1" />
                编辑
              </button>

              <button
                onClick={handleRegenerate}
                disabled={generatingTabs.has(activeTab) || !fileContent}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="重新生成当前内容"
              >
                <ArrowPathIcon className="w-3.5 h-3.5 mr-1" />
                生成
              </button>

              <button
                onClick={handleOptimize}
                disabled={generatingTabs.has(activeTab)}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="智能优化当前内容"
              >
                <SparklesIcon className="w-3.5 h-3.5 mr-1" />
                智能优化
              </button>
            </>
          ) : (
            <button
              onClick={handleSave}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 border border-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              title="保存修改"
            >
              <CheckIcon className="w-4 h-4 mr-1" />
              保存
            </button>
          )}
        </div>

        {/* Tab内容 */}
        <div className="flex-1 flex flex-col min-h-0">
          {activeTab === 'overview' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div
                ref={overviewRef}
                className={`flex-1 w-full p-4 border border-gray-300 rounded-lg focus-within:ring-blue-500 focus-within:border-blue-500 bg-white shadow-sm min-h-0 ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}`}
              >
                {isEditing ? (
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="w-full h-full p-0 border-0 focus:ring-0 resize-none font-mono text-sm text-gray-900 overflow-y-auto"
                    placeholder="请输入内容..."
                  />
               ) : generatingTabs.has('overview') && !streamingOverview ? (
                  <LoadingSkeleton type="overview" />
                ) : (
                  <div className="prose prose-sm max-w-none text-gray-800">
                    {/* 优先1：流式内容（用 dangerouslySetInnerHTML 绕过 React 协调）*/}
                    {streamingOverview && generatingTabs.has('overview') ? (
                      <div key="overview-streaming" dangerouslySetInnerHTML={{ __html: streamingToHtml(streamingOverview) }} />
                    ) : /* 优先2：显示最终内容（已完成）*/ localOverview ? (
                      <ReactMarkdown
                        key="overview-final"
                        components={markdownComponents}
                        remarkPlugins={[remarkGfm]}
                      >
                        {localOverview}
                      </ReactMarkdown>
                    ) : /* 最后：显示空状态提示 */ (
                      <div key="overview-empty" className="flex items-center justify-center py-16 text-gray-400">
                        <div className="text-center">
                          <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-base">点击"开始解析"按钮开始分析</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'requirements' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div
                ref={requirementsRef}
                className={`flex-1 w-full p-4 border border-gray-300 rounded-lg focus-within:ring-green-500 focus-within:border-green-500 bg-white shadow-sm min-h-0 ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}`}
              >
                {isEditing ? (
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="w-full h-full p-0 border-0 focus:ring-0 resize-none font-mono text-sm text-gray-900 overflow-y-auto"
                    placeholder="请输入内容..."
                  />
               ) : generatingTabs.has('requirements') && !streamingRequirements ? (
                  <LoadingSkeleton type="requirements" />
                ) : (
                  <div className="prose prose-sm max-w-none text-gray-800">
                    {/* 优先1：流式内容（用 dangerouslySetInnerHTML 绕过 React 协调）*/}
                    {streamingRequirements && generatingTabs.has('requirements') ? (
                      <div key="requirements-streaming" dangerouslySetInnerHTML={{ __html: streamingToHtml(streamingRequirements) }} />
                    ) : /* 优先2：显示最终内容（已完成）*/ localRequirements ? (
                      <ReactMarkdown
                        key="requirements-final"
                        components={markdownComponents}
                        remarkPlugins={[remarkGfm]}
                      >
                        {localRequirements}
                      </ReactMarkdown>
                    ) : /* 最后：显示空状态提示 */ (
                      <div key="requirements-empty" className="flex items-center justify-center py-16 text-gray-400">
                        <div className="text-center">
                          <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-base">点击"开始解析"按钮开始分析</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'outline' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div
                ref={outlineRef}
                className={`flex-1 w-full p-4 border border-gray-300 rounded-lg focus-within:ring-purple-500 focus-within:border-purple-500 bg-white shadow-sm min-h-0 ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}`}
              >
                {isEditing ? (
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="w-full h-full p-0 border-0 focus:ring-0 resize-none font-mono text-sm text-gray-900 overflow-y-auto"
                    placeholder="请输入内容..."
                  />
               ) : generatingTabs.has('outline') && !streamingOutline ? (
                  <LoadingSkeleton type="outline" />
                ) : (
                  <div className="prose prose-sm max-w-none text-gray-800">
                    {/* 优先1：流式内容（用 dangerouslySetInnerHTML 绕过 React 协调）*/}
                    {streamingOutline && generatingTabs.has('outline') ? (
                      <div key="outline-streaming" dangerouslySetInnerHTML={{ __html: streamingToHtml(streamingOutline) }} />
                    ) : /* 优先2：显示最终内容（已完成）*/ localOutlineReqs ? (
                      <ReactMarkdown
                        key="outline-final"
                        components={markdownComponents}
                        remarkPlugins={[remarkGfm]}
                      >
                        {localOutlineReqs}
                      </ReactMarkdown>
                    ) : /* 最后：显示空状态提示 */ (
                      <div key="outline-empty" className="flex items-center justify-center py-16 text-gray-400">
                        <div className="text-center">
                          <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-base">点击"开始解析"按钮开始分析</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'disqualification' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div
                ref={disqualificationRef}
                className={`flex-1 w-full p-4 border border-gray-300 rounded-lg focus-within:ring-red-500 focus-within:border-red-500 bg-white shadow-sm min-h-0 ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}`}
              >
                {isEditing ? (
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="w-full h-full p-0 border-0 focus:ring-0 resize-none font-mono text-sm text-gray-900 overflow-y-auto"
                    placeholder="请输入内容..."
                  />
               ) : generatingTabs.has('disqualification') && !streamingDisqualification ? (
                  <LoadingSkeleton type="disqualification" />
                ) : (
                  <div className="prose prose-sm max-w-none text-gray-800">
                    {/* 优先1：流式内容（用 dangerouslySetInnerHTML 绕过 React 协调）*/}
                    {streamingDisqualification && generatingTabs.has('disqualification') ? (
                      <div key="disqualification-streaming" dangerouslySetInnerHTML={{ __html: streamingToHtml(streamingDisqualification) }} />
                    ) : /* 优先2：显示最终内容（已完成）*/ localDisqualification ? (
                      <ReactMarkdown
                        key="disqualification-final"
                        components={markdownComponents}
                        remarkPlugins={[remarkGfm]}
                      >
                        {localDisqualification}
                      </ReactMarkdown>
                    ) : /* 最后：显示空状态提示 */ (
                      <div key="disqualification-empty" className="flex items-center justify-center py-16 text-gray-400">
                        <div className="text-center">
                          <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-base">点击"开始解析"按钮开始分析</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 分项报价 */}
          {activeTab === 'quotation' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div
                ref={quotationRef}
                className={`flex-1 w-full p-4 border border-gray-300 rounded-lg focus-within:ring-orange-500 focus-within:border-orange-500 bg-white shadow-sm min-h-0 ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}`}
              >
                {isEditing ? (
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="w-full h-full p-0 border-0 focus:ring-0 resize-none font-mono text-sm text-gray-900 overflow-y-auto"
                    placeholder="请输入内容..."
                  />
                ) : generatingTabs.has('quotation') && !streamingQuotation ? (
                  <LoadingSkeleton type="quotation" />
                ) : (
                  <div className="prose prose-sm max-w-none text-gray-800">
                    {/* 优先1：流式内容（用 dangerouslySetInnerHTML 绕过 React 协调）*/}
                    {streamingQuotation && generatingTabs.has('quotation') ? (
                      <div key="quotation-streaming" dangerouslySetInnerHTML={{ __html: streamingToHtml(streamingQuotation) }} />
                    ) : /* 优先2：显示最终内容（已完成）*/ localQuotation ? (
                      <ReactMarkdown
                        key="quotation-final"
                        components={markdownComponents}
                        remarkPlugins={[remarkGfm]}
                      >
                        {localQuotation}
                      </ReactMarkdown>
                    ) : /* 最后：显示空状态提示 */ (
                      <div key="quotation-empty" className="flex items-center justify-center py-16 text-gray-400">
                        <div className="text-center">
                          <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-base">点击"开始解析"按钮提取报价要求</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 招标模版 */}
          {activeTab === 'templates' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div
                ref={templatesRef}
                className={`flex-1 w-full p-4 border border-gray-300 rounded-lg focus-within:ring-indigo-500 focus-within:border-indigo-500 bg-white shadow-sm min-h-0 ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}`}
              >
                {isEditing ? (
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="w-full h-full p-0 border-0 focus:ring-0 resize-none font-mono text-sm text-gray-900 overflow-y-auto"
                    placeholder="请输入内容..."
                  />
                ) : generatingTabs.has('templates') && !streamingTemplates ? (
                  <LoadingSkeleton type="templates" />
                ) : (
                  <div className="prose prose-sm max-w-none text-gray-800 
                    [&_pre]:bg-transparent [&_pre]:p-0 [&_pre]:border-0 [&_pre]:shadow-none
                    [&_code]:bg-transparent [&_code]:p-0 [&_code]:before:content-none [&_code]:after:content-none">
                    {/* 优先1：流式内容（用 dangerouslySetInnerHTML 绕过 React 协调）*/}
                    {streamingTemplates && generatingTabs.has('templates') ? (
                      <div key="templates-streaming" dangerouslySetInnerHTML={{ __html: streamingToHtml(streamingTemplates.replace(/_/g, '\\_')) }} />
                    ) : /* 优先2：显示最终内容（已完成）*/ localTemplates ? (
                      <ReactMarkdown
                        key="templates-final"
                        components={templatesMarkdownComponents}
                        remarkPlugins={[remarkGfm]}
                      >
                        {/* 转义下划线，防止被解析为强调 */}
                        {localTemplates.replace(/_/g, '\\_')}
                      </ReactMarkdown>
                    ) : /* 最后：显示空状态提示 */ (
                      <div key="templates-empty" className="flex items-center justify-center py-16 text-gray-400">
                        <div className="text-center">
                          <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-base">点击"开始解析"按钮提取招标模版</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      {onNext && (
        <div className="flex justify-between items-center pt-4 border-t border-gray-200 mt-4">
          <button
            onClick={onPrev}
            className="flex items-center px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            上一步：项目配置
          </button>
          <button
            onClick={() => {
              if (hasEmptyRequiredTab) {
                setToast({ show: true, type: 'warning', title: '请先完成招标解析', messages: ['以下内容尚未生成：' + emptyTabNames.join('、')] });
                return;
              }
              onNext?.();
            }}
            disabled={nextDisabled}
            className={`flex items-center px-6 py-2.5 rounded-lg transition-colors font-medium ${
              hasEmptyRequiredTab
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            下一步：分项报价
            <ArrowRightIcon className="w-5 h-5 ml-2" />
          </button>
        </div>
      )}
      
      {/* Token 欠费弹窗 */}
      <TokenAlertDialog
        isOpen={tokenAlert.show}
        type={tokenAlert.type}
        title={tokenAlert.title}
        message={tokenAlert.message}
        onRecharge={rechargeTokens}
        onClose={closeTokenAlert}
      />
    </div>
  );
};

export default DocumentAnalysis;
